import React, { useState } from 'react';
import Home from '../Home';

const SoftwareDevelopment = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqItems = [
    {
      question: "What is software development?",
      answer: "Software development is the process of creating, designing, deploying, and maintaining software. It involves a structured process that includes research, design, programming, testing, and documentation."
    },
    {
      question: "Why is custom software development important for businesses?",
      answer: "Custom software is tailored to a business's unique needs, offering a competitive advantage by improving efficiency, automating processes, and providing a scalable solution that off-the-shelf software cannot."
    },
    {
      question: "What are the key stages in the software development lifecycle?",
      answer: "The key stages typically include planning, analysis, design, development, testing, and deployment. These steps ensure a comprehensive and well-managed process from start to finish."
    },
    {
      question: "What ongoing support is provided after the software is deployed?",
      answer: "We provide ongoing support that includes bug fixes, updates, security patches, and new feature development to ensure the software remains reliable, secure, and up-to-date."
    },
    {
      question: "How long does it take to develop a custom software application?",
      answer: "The timeline for a custom application depends on its complexity, features, and the number of developers involved. We will provide a detailed timeline after the initial consultation."
    },
    {
      question: "What is the difference between web development and software development?",
      answer: "Web development is a subset of software development focused on creating websites and web-based applications that run in a browser. Software development is a broader term that includes web, mobile, desktop, and other types of applications."
    },
    {
      question: "How do you ensure the security of custom software applications?",
      answer: "We implement industry-standard security practices, including data encryption, secure authentication, and regular security audits to protect your application from vulnerabilities and threats."
    },
    {
      question: "What is the cost of custom software development?",
      answer: "The cost of a custom application varies widely depending on its features, complexity, and scope. We will provide a transparent and detailed quote after we understand your project requirements."
    }
  ];

  return (
    <Home>
      <div className="font-sans text-gray-800 leading-relaxed">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-50 to-gray-100 py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-orange-500/10"></div>
          <div className="container mx-auto px-4 flex flex-col md:flex-row items-center justify-between relative z-10">
            <div className="md:w-1/2 mb-8 md:mb-0 animate-fade-in-up">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight">
                Crafting Tomorrow's Technology:<br />
                <span className="text-blue-600">Custom Software Development</span>
              </h1>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                At SRK Software, we specialize in crafting personalized software solutions that 
                cater to your unique needs. We help businesses innovate, streamline their operations, 
                and engage their audience with cutting-edge technology.
              </p>
              <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-8 rounded-xl shadow-2xl transition-all duration-300 transform hover:scale-105 hover:shadow-2xl">
                <span className="flex items-center">
                  CONTACT US NOW
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </span>
              </button>
            </div>
            <div className="md:w-1/2 animate-fade-in-up delay-200">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl w-full h-80 md:h-96 flex items-center justify-center relative overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10 text-center text-white">
                  <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                  <span className="text-xl font-semibold">Software Development</span>
                </div>
                <div className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full animate-pulse"></div>
                <div className="absolute bottom-4 left-4 w-6 h-6 bg-white/20 rounded-full animate-pulse delay-300"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-20 bg-white relative">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-50/50 to-transparent"></div>
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-2xl md:text-3xl italic font-semibold text-gray-700 mb-12 max-w-4xl mx-auto leading-relaxed">
              "We take pride in transforming businesses and assisting others in shaping their future through innovative software solutions."
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Feature 1 */}
              <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div className="bg-gradient-to-br from-blue-100 to-blue-200 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">Problem Solving and Innovation</h3>
                <p className="text-gray-600 leading-relaxed">
                  Software development revolves around problem-solving and finding creative solutions. 
                  We analyze your requirements and design solutions tailored to your specific needs.
                </p>
              </div>
              
              {/* Feature 2 */}
              <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div className="bg-gradient-to-br from-green-100 to-green-200 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">Collaboration and Communication</h3>
                <p className="text-gray-600 leading-relaxed">
                  Successful software development requires a partnership between clients, developers, and testers. 
                  We ensure a transparent and collaborative process from start to finish.
                </p>
              </div>
              
              {/* Feature 3 */}
              <div className="group bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div className="bg-gradient-to-br from-purple-100 to-purple-200 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">Continuous Learning and Adaptation</h3>
                <p className="text-gray-600 leading-relaxed">
                  The field of software development is constantly evolving. We embrace new technologies, 
                  frameworks, and tools to ensure we're always at the forefront of innovation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Development Process Section */}
        <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
          <div className="container mx-auto px-4 flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-12 md:mb-0 animate-fade-in-left">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl w-full h-80 md:h-96 flex items-center justify-center relative overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10 text-center text-white">
                  <svg className="w-24 h-24 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-xl font-semibold">Development Process</span>
                </div>
              </div>
            </div>
            <div className="md:w-1/2 md:pl-12 animate-fade-in-right">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-6">
                WHY YOU SHOULD GO FOR <br />
                <span className="text-blue-600">SOFTWARE DEVELOPMENT</span>
              </h2>
              <p className="text-gray-700 mb-6 text-lg leading-relaxed">
                Choosing software development opens the door to a world of endless opportunities. 
                In today's rapidly evolving industry, you'll find the opportunity to unleash your 
                creativity, solve complex problems, and build a rewarding career.
              </p>
              <ul className="space-y-4">
                {['Efficiency and Automation', 'Innovation and Competitive Advantage', 'Data Management and Decision-Making'].map((item, index) => (
                  <li key={index} className="flex items-center text-gray-700 group">
                    <div className="bg-green-100 rounded-full p-2 mr-4 group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Key Insights Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="bg-gradient-to-br from-gray-900 to-blue-900 rounded-3xl shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center py-6 font-bold text-2xl md:text-3xl">
                Key Insights about Software Development
              </div>
              <div className="p-8 md:p-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                  <div className="animate-fade-in-up">
                    <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl w-full h-64 md:h-80 flex items-center justify-center relative overflow-hidden shadow-2xl">
                      <div className="absolute inset-0 bg-black/30"></div>
                      <div className="relative z-10 text-center text-white">
                        <svg className="w-20 h-20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xl font-semibold">Code Insights</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-6 text-white">
                      Software development is a dynamic and intricate process that powers our digital world.
                    </h2>
                    <h3 className="text-xl md:text-2xl font-semibold mb-8 text-blue-200">
                      Understanding its fundamental aspects is crucial:
                    </h3>
                    <ul className="space-y-4">
                      {[
                        { icon: '🔍', text: "Problem Solving: At its core, software development is about solving real-world problems." },
                        { icon: '👥', text: "Collaboration: Software development is rarely a solitary endeavor." },
                        { icon: '💻', text: "Code/Syntax: Developers use coding languages like Python, Java, or JavaScript." },
                        { icon: '🎨', text: "UI/UX Design: User experience design is a key element for intuitive software." },
                        { icon: '🔄', text: "Iterative Process: Software development follows an iterative cycle for quality enhancement." }
                      ].map((item, index) => (
                        <li key={index} className="flex items-start text-white group">
                          <span className="text-2xl mr-4 group-hover:scale-110 transition-transform duration-300">{item.icon}</span>
                          <span className="text-lg leading-relaxed">{item.text}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Get answers to common questions about our software development services and processes.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto space-y-4">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-6 md:p-8 text-left hover:bg-gray-50 transition-colors duration-200"
                  >
                    <span className="text-lg md:text-xl font-semibold text-gray-800 pr-4">{item.question}</span>
                    <span className={`text-2xl text-blue-600 transition-transform duration-300 ${activeFaq === index ? 'rotate-45' : ''}`}>
                      +
                    </span>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${activeFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="p-6 md:p-8 pt-0 border-t border-gray-100">
                      <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
              Ready to Transform Your Business?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
              Let's discuss your project and create a custom software solution that drives your business forward.
            </p>
            <button className="bg-white text-blue-600 hover:bg-gray-100 font-bold py-4 px-8 rounded-xl shadow-2xl transition-all duration-300 transform hover:scale-105">
              <span className="flex items-center justify-center">
                START YOUR PROJECT TODAY
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
            </button>
          </div>
        </section>
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out;
        }
        
        .animate-fade-in-left {
          animation: fadeInLeft 0.8s ease-out;
        }
        
        .animate-fade-in-right {
          animation: fadeInRight 0.8s ease-out;
        }
        
        .delay-200 {
          animation-delay: 0.2s;
        }
        
        .delay-300 {
          animation-delay: 0.3s;
        }
      `}</style>
    </Home>
  );
};

export default SoftwareDevelopment;